import { FinishBy } from "./guest/time";
import { FlowFrame, requestStepFocus } from "../../components/PageShell";
import { useNow } from "../../lib/useNow";
import { usePageTitle } from "../../../../../shared/hooks/usePageTitle";
import { useEffect, useId, useRef, type ChangeEvent } from "react";
import { useKeyboardViewport } from "../../lib/useKeyboardViewport";
import { Link } from "react-router-dom";
import { useTranslation } from "../../i18n/useTranslation";
import type { PublicProfessionalProfile } from "../../features/profile/operations";
import type { useFlowState } from "../../features/booking/useFlowState";
import { serviceSummary, StepLeadBar, StepFoot, StepMeta } from "./StepChrome";

/** What this screen collects. Every field is optional here because "not typed
 *  yet" and "typed and then cleared" are different things — see `account`. */
export interface BookingDetails {
  name?: string;
  email?: string;
  // PHONE AND NOTES ARE DELIBERATELY ABSENT.
  //
  // The comps have both and both were built — then removed on 2026-08-26 when
  // the API turned out to carry neither. `GuestContactInput` has firstName,
  // lastName and email; the schema says the phone omission is deliberate (no
  // SMS transport, and the only validator is US-only), and there is no client
  // notes field anywhere on the public schema.
  //
  // Removed rather than left collecting, because the hints PROMISED delivery —
  // "Shared with {business} only", over a placeholder reading "Allergies,
  // parking, running late". A client could have typed a severe allergy, been
  // told the professional would see it, and had it silently discarded. A
  // missing field is a gap; a field that lies is a hazard.
  //
  // Founder's ruling: skipped for now, revisited after launch. See Q33.
}

export type DetailsField = keyof BookingDetails;

/** Only the two required fields can fail, so this is the whole error space. */
export type RequiredField = "name" | "email";

/** The signed-in client, when there is one. Not the professional. */
export interface ClientAccount {
  name: string;
  email: string;
}

interface Props {
  slug: string;
  profile: PublicProfessionalProfile;
  flow: ReturnType<typeof useFlowState>;
  /** wired in Stage 3 — what the client has typed so far. */
  values: BookingDetails;
  /** wired in Stage 3 — null for a guest, which is the default state. */
  account?: ClientAccount | null;
  /**
   * Fields the parent's validation rejected.
   *
   * THE CLEARING CONTRACT MATTERS MORE THAN THE SETTING ONE. A non-empty
   * `invalid` disables Continue, and a disabled default button also kills
   * implicit submission — so Enter stops working too. A parent that only
   * recomputes this on submit therefore strands the client: they fix both
   * fields and have no remaining way to go forward. Recompute it whenever the
   * field it names changes. wired in Stage 3.
   */
  invalid?: readonly RequiredField[];
  /**
   * Set when the SERVER rejected these fields, not the local form.
   *
   * A changing value per rejection, so arriving here from a server refusal
   * moves focus to the offending field even though this component mounted
   * fresh — see the focus effect. Null for an ordinary visit, which must NOT
   * seize focus or throw up a phone keyboard.
   */
  serverRejected?: string | null;
  /** wired in Stage 3 */
  onChange: (field: DetailsField, value: string) => void;
  /** wired in Stage 3 */
  onContinue: () => void;
  /**
   * GUEST VERIFICATION ONLY: the email is about to receive a code, so the hint
   * says so (Product, 2026-09-22). Absent on today's path, which sends no code
   * and keeps its own approved hint.
   */
  verifiedGuest?: boolean;
  /**
   * Seconds left on the SERVER'S hold, or null when there is none to show.
   *
   * Product requires this step to carry the countdown, and it is derived from
   * the hold's `expiresAt` rather than a timer started here — a re-hold RESUMES
   * an expiry, it does not restart it, so a client-side clock would tell the
   * visitor a different story from the one the server will act on.
   *
   * It was absent, and the cost was measurable: someone typing their name had
   * no indication the slot was going, and the journey only failed closed two
   * screens later at review.
   */
  secondsLeft?: number | null;
  /** Backend hold deadline; never reconstructed from seconds remaining. */
  holdExpiresAt?: string | null;
  /** The server's hold is gone. Forward submission stops here, not at review. */
  holdExpired?: boolean;
}

// Step 3 of 4 — who the booking is for.
//
// Follows docs/design/lura-book-v1 StepDetails and all three of its states:
// `guest` (the default), `invalid`, and `account`. They are DERIVED here rather
// than taken as a `state` prop the way the comp declares them, because in
// running code the state is a consequence of the data: an account was passed or
// it wasn't, validation rejected something or it didn't. A `state` prop
// alongside both would be a second source of truth that could disagree with
// them.
//
// The values are the PARENT's, not this screen's. Details are needed again at
// review and at confirm, so a screen holding them locally would have to hoist
// them the moment Stage 3 arrives — and they cannot go where the rest of the
// flow state lives, because useFlowState keeps that in the URL and a name,
// email and phone number do not belong in a link somebody might share.

/**
 * One labelled input.
 *
 * A LABEL, NOT A PLACEHOLDER. The placeholder is the example; the label is the
 * question, and it has to survive the field being filled in. Every input here
 * is tied to a real `<label>` by id, describes itself through the hint or the
 * error via `aria-describedby`, and announces its own failure with
 * `role="alert"` rather than relying on a client noticing red.
 *
 * Wants to live in StepChrome next to the other kit primitives. It is local to
 * this file because StepChrome belongs to the lead — see the note in the
 * hand-back.
 */
function Field({
  id,
  label,
  value,
  placeholder,
  hint,
  error,
  required,
  area,
  autoComplete,
  type = "text",
  testId,
  onChange,
  readOnly = false,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  area?: boolean;
  autoComplete?: string;
  type?: string;
  testId: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}) {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  // The design shows the hint OR the error, never both, so the description
  // points at whichever is actually on screen. Describing an element that is
  // not rendered reads as silence to a screen reader.
  const describedBy = error ? errorId : hint ? hintId : undefined;

  const shared = {
    id,
    value,
    placeholder,
    "aria-required": required || undefined,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy,
    autoComplete,
    readOnly,
    "data-testid": testId,
    className: `b-input${error ? " is-bad" : ""}${area ? " is-area" : ""}`,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(event.target.value),
  };

  return (
    <div className="b-field">
      <label htmlFor={id}>{label}</label>
      {area ? <textarea rows={3} {...shared} /> : <input type={type} {...shared} />}
      {error ? (
        <span className="b-err" id={errorId} role="alert" data-testid={`${testId}-error`}>
          {error}
        </span>
      ) : null}
      {!error && hint ? (
        // `data-testid` to match the error's, so the helper can be asserted by
        // its own element rather than by scanning the step's text. The design
        // shows the hint OR the error, never both, which is what makes the two
        // worth naming separately.
        <p className="b-meta" id={hintId} data-testid={`${testId}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Mirrors StepTime's and StepReview's. Clamped at zero: a hold that ticks a
 * second past its expiry before the parent notices would otherwise render a
 * negative countdown on the screen the visitor is typing into.
 */
function mmss(seconds: number): string {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  return `${minutes}:${String(safe % 60).padStart(2, "0")}`;
}

function StepDetails({
  slug,
  profile,
  flow,
  values,
  account = null,
  invalid = [],
  serverRejected = null,
  onChange,
  onContinue,
  secondsLeft = null,
  holdExpiresAt = null,
  holdExpired = false,
  verifiedGuest = false,
}: Props) {
  const { t, fmt } = useTranslation();
  // The phone keyboard never covers Continue (lib/keyboardViewport.ts).
  useKeyboardViewport();
  const deadlineMs = holdExpiresAt ? Date.parse(holdExpiresAt) : NaN;
  const deadlineNow = useNow(Number.isFinite(deadlineMs) ? deadlineMs : null);
  usePageTitle(account ? t("flow.details.account.heading") : t("guest.review.contact"));
  const fieldId = useId();
  const business = profile.businessName;

  const { serviceId, startTime } = flow.selection;
  const summary = serviceSummary(profile, serviceId, fmt);
  const when = startTime
    ? `${fmt.date(startTime, profile.timezone)} · ${fmt.time(startTime, profile.timezone)}`
    : null;

  // Account bookings carry the server's stored contact, not guestContact. An
  // editable value here would be ignored by createBooking and mislead clients.
  const name = account?.name ?? values.name ?? "";
  const email = account?.email ?? values.email ?? "";

  const nameError = invalid.includes("name") ? t("flow.details.name.error") : null;
  const emailError = invalid.includes("email") ? t("flow.details.email.error", { business }) : null;
  /*
   * BLOCKED BY EITHER, and the hold half is the new one.
   *
   * A dead hold cannot be committed, so Continue must not carry the visitor
   * forward into a review screen that can only refuse them. Everything they
   * typed stays: `values` lives in the parent's state and this step writes
   * nothing back, so the fields survive the trip to the time step and back.
   */
  const blocked = invalid.length > 0 || holdExpired;
  const backToTime = `/${slug}/book/time${window.location.search}`;
  const heldLabel = !holdExpired && secondsLeft !== null ? mmss(secondsLeft) : null;

  // WHERE FOCUS GOES WHEN A SUBMIT IS REJECTED.
  //
  // Rejecting a submit disables Continue, and a browser blurs an element it
  // disables — so without this, focus falls to `<body>` and a keyboard or
  // screen-reader client is returned to the top of the document, several
  // screens away from the field the alert just described. They hear what is
  // wrong and are standing nowhere near it.
  //
  // FIRED ON THE TRANSITION INTO REJECTION, not on which field is first. The
  // difference is not academic. Keying this on the first invalid field moves
  // focus again as `invalid` SHRINKS: reject with both fields empty, start
  // typing a name, and the moment the parent drops "name" the first entry
  // becomes "email" and focus jumps out of the field mid-word, with the rest of
  // the name landing in the email box. Seeding the ref from the first render
  // also means arriving with `invalid` already populated does not grab focus
  // and throw up the keyboard on a phone — nobody submitted anything yet.
  //
  // Looked up by id rather than through refs: the ids are already deterministic
  // here, and threading a ref through `Field` would put one on all four fields
  // to serve the two that can fail.
  // THE SERVER-REJECTION CASE BREAKS THE TRANSITION RULE, and needs its own.
  //
  // When the SERVER rejects a name or email, the flow navigates back here from
  // Review — which swaps StepReview for StepDetails at the same position, so
  // this component MOUNTS FRESH with `invalid` already populated. `blocked` is
  // true on render 1, `wasRejected` seeds true, and the transition never
  // happens: focus stays on `<body>` and the client is dropped at the top of a
  // document, which is the exact harm the effect above exists to prevent.
  //
  // Worse, a unit test pinned that as correct — it could not tell a fresh mount
  // caused by a server rejection from a restored tab, because from inside this
  // component they look identical. They are not: one had a submit rejected
  // moments ago, the other had nobody submit anything.
  //
  // So the parent says which it is. `serverRejected` changes identity per
  // rejection, so a second rejection of the same field focuses again.
  const firstInvalid = invalid[0] ?? null;
  const wasRejected = useRef(blocked);
  useEffect(() => {
    const rejectedOnMount = serverRejected !== null;
    if (firstInvalid && blocked && (rejectedOnMount || !wasRejected.current)) {
      document.getElementById(`${fieldId}-${firstInvalid}`)?.focus();
    }
    wasRejected.current = blocked;
  }, [blocked, fieldId, firstInvalid, serverRejected]);

  return (
    <FlowFrame
      testId="step-details"
      // The design's three states, plus the one the hold added. `blocked` now
      // means EITHER a rejected field or a dead hold, and reporting a dead hold
      // as "invalid" would name the wrong cause to anything reading this.
      data={{
        state: holdExpired ? "expired" : invalid.length > 0 ? "invalid" : account ? "account" : "guest",
      }}
      lead={<StepLeadBar backTo={backToTime} step={3} of={verifiedGuest ? 5 : 4} v3 />}
    >

      <div className="b-scroll">

        {/*
          THE HOLD DIED WHILE THEY WERE TYPING.
          ────────────────────────────────────────────────────────────────────
          Product's requirement, and the four parts are each doing work: keep
          what they entered, stop them going forward, say plainly that nothing
          was booked, and give them one way on.

          The title and body are the APPROVED expired copy already used at
          review — the same fact on the same journey, and "Nothing was booked —
          your service, day and details are all still here" is literally true
          here too. Writing a second sentence for it would put a second author
          on the one thing the visitor most needs to believe.

          `role="alert"`: unlike the review notice, this arrives while they are
          mid-task and the whole step has just stopped accepting input.
        */}
        {holdExpired ? (
          <div className="b-notice t-warning" role="alert" data-testid="details-expired">
            <div className="b-notice-b">
              <b>{t("flow.review.expired.title")}</b>
              {t("flow.review.expired.body") ? <p className="b-p">{t("flow.review.expired.body", { time: when ?? "" })}</p> : null}
              <div className="b-notice-a">
                <Link className="b-btn v-secondary s-sm" to={backToTime} onClick={() => requestStepFocus()}>
                  {t("flow.details.expired.action")}
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        <div className="b-stack g6">
          <h1 className="b-display" tabIndex={-1}>
            {account ? t("flow.details.account.heading") : t("guest.review.contact")}
          </h1>
          {summary && when ? (
            <p className="b2-sub" data-testid="details-appointment-summary">
              {summary} · {when}
            </p>
          ) : null}

        </div>

        {/*
          `noValidate`, so the browser's own validation bubble never pre-empts
          the copy below with "Please fill out this field" — the generic message
          this screen exists to avoid, and one that disappears on the next tap
          where an inline error stays readable for as long as the client needs
          it. `aria-required` rather than `required` then follows from it:
          constraint validation is off, so `required` would carry no behaviour
          at all and only the ARIA state would still be doing work.

          The submit button is in the sticky footer, outside this form, and is
          associated back to it by `form=` so Enter submits from any field. The
          alternative was moving the footer inside the form, which would have
          meant editing chrome this track does not own.
        */}
        <form
          id={fieldId}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            // Enter submits from any field, so the guard has to live here as
            // well as on the button — the same reason the commit screen's
            // handler carries one.
            if (holdExpired) return;
            onContinue();
          }}
        >
          <div className="b-stack g18">
            <Field
              id={`${fieldId}-name`}
              testId="details-name"
              label={t("flow.details.name.label")}
              placeholder={t("flow.details.name.placeholder")}
              value={name}
              readOnly={!!account}
              error={nameError}
              required
              autoComplete="name"
              onChange={(next) => onChange("name", next)}
            />
            <Field
              id={`${fieldId}-email`}
              testId="details-email"
              type="email"
              label={t("flow.details.email.label")}
              placeholder={t("flow.details.email.placeholder")}
              hint={account ? undefined : verifiedGuest ? t("guest.details.emailHint") : t("flow.details.email.hint")}
              value={email}
              readOnly={!!account}
              error={emailError}
              required
              autoComplete="email"
              onChange={(next) => onChange("email", next)}
            />
          </div>
        </form>

        {heldLabel !== null ? <FinishBy expiresAt={holdExpiresAt}
          timeZone={profile.timezone} now={deadlineNow} testId="details-finish-by" /> : null}
        <StepMeta />
      </div>

      <StepFoot>
        <button
          type="submit"
          form={fieldId}
          className={`b-btn v-primary is-full${blocked ? " is-off" : ""}`}
          data-testid="step-details-continue"
          disabled={blocked}
        >
          {t("flow.continue")}
        </button>
      </StepFoot>
    </FlowFrame>
  );
}

export default StepDetails;
